import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, STOCK_LABEL, STOCK_STYLE, fmtPrice } from "@/lib/api";
import { ProductDialog } from "@/pages/admin/ProductDialog";

export const ProductsTab = ({ onChange }) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); api.get("/brands").then((r) => setBrands(r.data)); }, []);

  const filtered = useMemo(() => products.filter((p) => !q || `${p.name} ${p.brand_name} ${p.category} ${p.size}`.toLowerCase().includes(q.toLowerCase())), [products, q]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.product_id}`);
    toast.success("Product deleted"); load(); onChange?.();
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter products…" className="sr-input pl-9 py-2" data-testid="admin-product-search" />
        </div>
        <button onClick={() => setEditing({})} className="sr-btn-blue !py-2" data-testid="admin-product-add-button"><Plus size={16} /> Add product</button>
      </div>
      <div className="mt-4 sr-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="bg-[#EBF3FE] text-[#1E40AF] text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-2.5">Product</th><th className="text-left px-3 py-2.5">Brand</th><th className="text-left px-3 py-2.5">Size</th><th className="text-left px-3 py-2.5">Case</th><th className="text-left px-3 py-2.5">Price</th><th className="text-left px-3 py-2.5">Stock</th><th className="text-left px-3 py-2.5">Active</th><th className="px-3 py-2.5"></th></tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-product-row-${p.product_id}`}>
                <td className="px-4 py-2.5 font-semibold text-[#0B1F4B]">{p.name}<p className="text-[11px] text-slate-400 font-normal">{p.category}{p.sku ? ` · ${p.sku}` : ""}</p></td>
                <td className="px-3 py-2.5 text-slate-600">{p.brand_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{p.size}</td>
                <td className="px-3 py-2.5 font-mono text-slate-600">{p.case_pack ?? "—"}</td>
                <td className="px-3 py-2.5 font-mono text-slate-600">{fmtPrice(p.price) || <span className="text-[#1E7CF2] text-xs">On request</span>}</td>
                <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${STOCK_STYLE[p.stock_status]}`}>{STOCK_LABEL[p.stock_status]}</span></td>
                <td className="px-3 py-2.5">{p.active ? <span className="text-emerald-600 text-xs font-semibold">Yes</span> : <span className="text-slate-400 text-xs">Hidden</span>}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="p-1.5 text-slate-500 hover:text-[#1E7CF2]" data-testid={`admin-product-edit-${p.product_id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 text-slate-500 hover:text-[#E63946]" data-testid={`admin-product-delete-${p.product_id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing !== null && <ProductDialog product={editing} brands={brands} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />}
    </div>
  );
};
